import * as Apollo from '@apollo/client';
import { useGsocApolloClient } from './GsocApolloProvider';
import type { DataProductApi } from '../constants/api';

// Re-export everything from @apollo/client so codegen imports work
export * from '@apollo/client';

interface ApiContext {
  apiName?: DataProductApi;
}

/**
 * Resolves the client option. Returns undefined if no GsocApolloProvider is present,
 * which lets Apollo fall back to the nearest ApolloProvider/MockedProvider (useful in tests).
 */
function useResolvedClient(context: unknown): Apollo.ApolloClient<object> | undefined {
  const apiName = (context as ApiContext | undefined)?.apiName;
  return useGsocApolloClient(apiName);
}

// Override hooks to resolve the correct client from context based on apiName

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useQuery<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.QueryHookOptions<TData, TVariables>,
): Apollo.QueryResult<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useQuery<TData, TVariables>(query, { ...options, ...(client && { client }) });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useLazyQuery<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.LazyQueryHookOptions<TData, TVariables>,
): Apollo.LazyQueryResultTuple<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useLazyQuery<TData, TVariables>(query, { ...options, ...(client && { client }) });
}

export function useSuspenseQuery<
  TData = unknown,
  TVariables extends Apollo.OperationVariables = Apollo.OperationVariables,
>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.SuspenseQueryHookOptions<TData, TVariables> | Apollo.SkipToken,
) {
  const context = options && typeof options !== 'symbol' ? options.context : undefined;
  const client = useResolvedClient(context);
  // When SkipToken is passed, we still need to call the hook unconditionally
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const resolvedOptions = options && typeof options !== 'symbol' ? { ...options, ...(client && { client }) } : options!;
  return Apollo.useSuspenseQuery<TData, TVariables>(query, resolvedOptions);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useMutation<TData = any, TVariables = Apollo.OperationVariables>(
  mutation: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.MutationHookOptions<TData, TVariables>,
): Apollo.MutationTuple<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useMutation<TData, TVariables>(mutation, { ...options, ...(client && { client }) });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSubscription<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  subscription: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.SubscriptionHookOptions<TData, TVariables>,
): Apollo.SubscriptionResult<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useSubscription<TData, TVariables>(subscription, { ...options, ...(client && { client }) });
}
